import {
    Alert,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    InputAdornment,
    Link,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ChineseTeacher, IUser, ScoringLevel, UserApi } from '../api/UserApi';
import { AudioApi } from '../api/AudioApi';
import background from '../asset/background-registration.png';
import { isJwtLoggedIn } from '../util/session';
import { pageWhiteCardSx } from '../style/pageWhiteCardSx';

import female_avatar_1 from '../asset/female_avatar_1.png';
import female_avatar_2 from '../asset/female_avatar_2.png';
import male_avatar from '../asset/male_avatar.png';

import { Visibility, VisibilityOff } from '@mui/icons-material';
import { ActivityEnum, useUserActivityTracker } from '../UserActivityTracker';
import { DialogPromptApi, DialogPromptScoringLevel } from '../api/DialogPromptApi';
import female_shilhouette from '../asset/female_silhouette.png';
import male_shilhouette from '../asset/male_silhouette.png';
import { handleErrorMessage } from '../component/ErrorDialog';
import { TeacherIntroductionFemale, TeacherIntroductionFemale2, TeacherIntroductionMale, getAudioByText } from '../component/dialogPrompt/DialogPromptComponent';
import AppBarContext, { AppBarMenuIndex } from '../context/AppBarContext';
import { useAudioPlayer } from '../context/AudioPlayerContext';
import { setUserLocalStorage } from './Login';

function buildSettingsPayload(
    chineseTeacher: string | null,
    avatar: string,
    learnChineseCharacter: boolean,
    onlyRequiredAudio: boolean,
    scoringLevel: string | null,
    isAudioSpeedFast: boolean,
): IUser {
    const p: IUser = {
        learn_chinese_character: learnChineseCharacter,
        only_required_audio: onlyRequiredAudio,
        audio_speed_fast: isAudioSpeedFast,
    };
    if (chineseTeacher) p.chinese_teacher = chineseTeacher as ChineseTeacher;
    if (avatar) p.avatar = avatar;
    if (scoringLevel) p.scoring_level = scoringLevel as ScoringLevel;
    return p;
}

export default function Settings() {
    useUserActivityTracker(ActivityEnum.Settings, null, null, isJwtLoggedIn());

    const { setErrorMessage, setIsLoggedIn } = React.useContext(AppBarContext);
    const history = useHistory();
    const location = useLocation();

    const { playAudio } = useAudioPlayer();

    const [email, setEmail] = React.useState('');
    const [firstName, setFirstName] = React.useState('');
    const [lastName, setLastName] = React.useState('');
    const [currentPassword, setCurrentPassword] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [confirmPassword, setConfirmPassword] = React.useState('');

    const [chineseTeacher, setChineseTeacher] = React.useState<string | null>(null);
    const [avatar, setAvatar] = React.useState('');
    const [learnChineseCharacter, setLearnChineseCharacter] = React.useState(true);
    const [onlyRequiredAudio, setOnlyRequiredAudio] = React.useState(true);
    const [scoringLevel, setScoringLevel] = React.useState<string | null>(null);
    const [isAudioSpeedFast, setIsAudioSpeedFast] = React.useState(true);

    const [user, setUser] = React.useState<IUser | null>(null);

    const [settingsError, setSettingsError] = React.useState('');
    const [settingsSuccess, setSettingsSuccess] = React.useState('');
    const [profileError, setProfileError] = React.useState('');
    const [profileSuccess, setProfileSuccess] = React.useState('');

    const [savingSettings, setSavingSettings] = React.useState(false);
    const [savingProfile, setSavingProfile] = React.useState(false);

    const { setMenuIndex } = React.useContext(AppBarContext);

    const [showCurrentPassword, setShowCurrentPassword] = React.useState(false);
    const [showNewPassword, setShowNewPassword] = React.useState(false);

    const [maleAudio, setMaleAudio] = React.useState<Blob | null | undefined>(undefined);
    const [femaleAudio, setFemaleAudio] = React.useState<Blob | null | undefined>(undefined);
    const [female2Audio, setFemale2Audio] = React.useState<Blob | null | undefined>(undefined);

    const [dialogPromptScoringLevel, setDialogPromptScoringLevel] = React.useState<DialogPromptScoringLevel | null>(null);

    const applyUserToForm = React.useCallback((u: IUser) => {
        if (u.email) setEmail(u.email);
        setFirstName(u.first_name ?? '');
        setLastName(u.last_name ?? '');
        if (u.avatar) setAvatar(u.avatar);
        if (u.chinese_teacher) setChineseTeacher(u.chinese_teacher);
        if (u.learn_chinese_character != null && u.learn_chinese_character !== undefined) {
            setLearnChineseCharacter(u.learn_chinese_character);
        }
        if (u.only_required_audio !== null && u.only_required_audio !== undefined) {
            setOnlyRequiredAudio(Boolean(u.only_required_audio));
        }
        if (u.scoring_level) setScoringLevel(u.scoring_level);
        if (u.audio_speed_fast !== null && u.audio_speed_fast !== undefined) {
            setIsAudioSpeedFast(Boolean(u.audio_speed_fast));
        }
    }, []);

    const settingsDirty = React.useMemo(() => {
        if (!user) return false;
        return (
            (chineseTeacher ?? '') !== (user.chinese_teacher ?? '') ||
            avatar !== (user.avatar ?? '') ||
            learnChineseCharacter !== Boolean(user.learn_chinese_character) ||
            onlyRequiredAudio !== Boolean(user.only_required_audio) ||
            (scoringLevel ?? '') !== (user.scoring_level ?? '') ||
            isAudioSpeedFast !== Boolean(user.audio_speed_fast)
        );
    }, [user, chineseTeacher, avatar, learnChineseCharacter, onlyRequiredAudio, scoringLevel, isAudioSpeedFast]);

    const profileDirty = React.useMemo(() => {
        if (!user) return false;
        const newPasswordRequested = password.trim() !== '' || confirmPassword.trim() !== '';
        return (
            firstName !== (user.first_name ?? '') ||
            lastName !== (user.last_name ?? '') ||
            email !== (user.email ?? '') ||
            newPasswordRequested
        );
    }, [user, firstName, lastName, email, password, confirmPassword]);

    const refreshUser = React.useCallback(() => {
        return UserApi.get().then((r) => {
            if (r.status < 300 && r.data) {
                const u = r.data;
                setUser(u);
                setUserLocalStorage(u);
                applyUserToForm(u);
            }
        });
    }, [applyUserToForm]);

    React.useEffect(() => {
        setMenuIndex(AppBarMenuIndex.Settings);

        if (!isJwtLoggedIn()) {
            history.replace({ pathname: '/login', state: { from: { pathname: '/settings' } } });
            return;
        }

        getAudioByText(history, setErrorMessage, TeacherIntroductionMale, setMaleAudio, false, 'male');
        getAudioByText(history, setErrorMessage, TeacherIntroductionFemale, setFemaleAudio, false, 'female');
        getAudioByText(history, setErrorMessage, TeacherIntroductionFemale2, setFemale2Audio, false, 'female2');

        UserApi.get()
            .then((response) => {
                const u: IUser = response.data;
                if (u) {
                    setUser(u);
                    applyUserToForm(u);
                }
            })
            .catch((err) => {
                setProfileError('Error loading account: ' + (err.response?.data?.message ?? err.message));
            });

        DialogPromptApi.getAll(undefined, 'DialogPromptScoringLevelModel')
            .then((response) => {
                if (response.status >= 300) {
                    throw new Error('not ok statuscode=' + response.status + ' ' + response.statusText);
                }
                return response.data;
            })
            .then((list) => {
                if (list.length <= 0) {
                    setErrorMessage('Scoring Level not found');
                } else {
                    const visibleItemList = list.filter((item) => !item.is_hidden).sort((a, b) => b.id - a.id);
                    if (visibleItemList.length > 0) {
                        setDialogPromptScoringLevel(visibleItemList[0] as DialogPromptScoringLevel);
                    }
                }
            })
            .catch((err) => {
                handleErrorMessage(err, setErrorMessage, history, location);
            });
    }, [applyUserToForm, history, location, setErrorMessage, setMenuIndex]);

    function playTeacherIntroductionAudio(teacher: string) {
        if (teacher == ChineseTeacher.Male && maleAudio) {
            playAudio(maleAudio);
        } else if (teacher == ChineseTeacher.Female && femaleAudio) {
            playAudio(femaleAudio);
        } else if (teacher == ChineseTeacher.Female2 && female2Audio) {
            playAudio(female2Audio);
        } else {
            const storedOnlyRequiredAudio = localStorage.getItem('onlyRequiredAudio');
            const ora = storedOnlyRequiredAudio ? JSON.parse(storedOnlyRequiredAudio) : true;
            if (ora == false) {
                throw Error('unknown selected teacher name in local storage ' + teacher);
            }
        }
    }

    const avatarOptions = [male_shilhouette, female_shilhouette];

    const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            const reader = new FileReader();
            reader.onload = function (e) {
                const result = e.target && e.target.result ? e.target.result.toString() : '';
                setAvatar(result);
            };
            reader.readAsDataURL(event.target.files[0]);
        }
    };

    const handleSaveSettings = (e: React.FormEvent) => {
        e.preventDefault();
        setSettingsError('');
        setSettingsSuccess('');
        if (!settingsDirty) {
            setSettingsError('No preference changes to save.');
            return;
        }
        setSavingSettings(true);
        const payload = buildSettingsPayload(
            chineseTeacher,
            avatar,
            learnChineseCharacter,
            onlyRequiredAudio,
            scoringLevel,
            isAudioSpeedFast,
        );
        UserApi.update(payload)
            .then((response) => {
                if (response.status < 300) {
                    if (payload.chinese_teacher && payload.chinese_teacher !== user?.chinese_teacher) {
                        AudioApi.setSelectedCustomSentenceTeacher(payload.chinese_teacher);
                    }
                    if (payload.audio_speed_fast !== user?.audio_speed_fast) {
                        AudioApi.notifyCustomSentencePlaybackSettingsChanged();
                    }
                    setSettingsSuccess('Preferences saved.');
                    void refreshUser();
                } else {
                    throw new Error(String(response.status));
                }
            })
            .catch((err) => {
                setSettingsError(err.response?.data?.message ?? 'Could not save preferences.');
            })
            .finally(() => {
                setSavingSettings(false);
            });
    };

    const handleSaveProfile = (e: React.FormEvent) => {
        e.preventDefault();
        setProfileError('');
        setProfileSuccess('');
        if (!profileDirty) {
            setProfileError('Change your name, email, or password before saving.');
            return;
        }
        if (!currentPassword.trim()) {
            setProfileError('Enter your current password to confirm these changes.');
            return;
        }
        if (password.trim() !== '' && password !== confirmPassword) {
            setProfileError('New password and confirmation do not match.');
            return;
        }
        setSavingProfile(true);
        const profilePayload: IUser = {
            first_name: firstName,
            last_name: lastName,
            email: email,
            current_password: currentPassword,
        };
        if (password.trim() !== '') {
            profilePayload.password = password;
        }
        UserApi.update(profilePayload)
            .then((response) => {
                if (response.status < 300) {
                    if (profilePayload.password) {
                        localStorage.removeItem('jwt');
                        setIsLoggedIn(false);
                        history.replace('/login', { passwordChanged: true });
                        return;
                    }
                    setProfileSuccess('Account updated.');
                    setCurrentPassword('');
                    setPassword('');
                    setConfirmPassword('');
                    void refreshUser();
                } else {
                    throw new Error(String(response.status));
                }
            })
            .catch((err) => {
                setProfileError(err.response?.data?.message ?? 'Could not update account.');
            })
            .finally(() => {
                setSavingProfile(false);
            });
    };

    const [validateEmailClicked, setValidateEmailClicked] = React.useState(false);
    const handleValidateEmail = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        setValidateEmailClicked(true);
    };
    const [snackbarOpen, setSnackbarOpen] = React.useState(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState('');
    const [snackbarSeverity, setSnackbarSeverity] = React.useState<'success' | 'error'>('success');
    React.useEffect(() => {
        if (validateEmailClicked) {
            UserApi.sendValidateEmail()
                .then(() => {
                    setValidateEmailClicked(false);
                    setSnackbarMessage('Verification email sent. Check your inbox.');
                    setSnackbarSeverity('success');
                    setSnackbarOpen(true);
                })
                .catch((err) => {
                    const message = err.response?.data?.message || 'Unknown error occurred';
                    setValidateEmailClicked(false);
                    setSnackbarMessage('Could not send email: ' + message);
                    setSnackbarSeverity('error');
                    setSnackbarOpen(true);
                });
        }
    }, [validateEmailClicked]);

    if (!isJwtLoggedIn()) {
        return null;
    }

    const emailVerified = Boolean(user?.read_only_email_validated);

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'center',
                backgroundImage: `url(${background})`,
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'auto 100%',
                backgroundPosition: 'end',
                minHeight: 'calc(100vh - 82px)',
                width: '100%',
                padding: '24px 16px 48px',
                boxSizing: 'border-box',
            }}
        >
            {!user ? (
                <CircularProgress sx={{ mt: 4 }} />
            ) : (
                <Stack spacing={3} sx={{ width: '100%', maxWidth: 640 }}>
                    <Typography variant="h2" component="h1" sx={{ textAlign: 'center' }}>
                        Account & preferences
                    </Typography>

                    <Paper
                        component="form"
                        elevation={0}
                        onSubmit={handleSaveSettings}
                        sx={{ p: 3, overflow: 'hidden', ...pageWhiteCardSx }}
                    >
                        <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Learning preferences
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Teacher voice, playback, avatar, and grading. Saves without your account password.
                        </Typography>

                        <Stack spacing={2}>
                            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
                                <Typography component="span" sx={{ width: 140, flexShrink: 0 }}>
                                    Teacher voice
                                </Typography>
                                <div style={{ display: 'flex', gap: '0.5em' }}>
                                    {[
                                        { key: ChineseTeacher.Male, src: male_avatar },
                                        { key: ChineseTeacher.Female, src: female_avatar_1 },
                                        { key: ChineseTeacher.Female2, src: female_avatar_2 },
                                    ].map(({ key, src }) => (
                                        <div
                                            key={key}
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => {
                                                setChineseTeacher(key);
                                                playTeacherIntroductionAudio(key);
                                            }}
                                            onKeyDown={(ev) => {
                                                if (ev.key === 'Enter' || ev.key === ' ') {
                                                    ev.preventDefault();
                                                    setChineseTeacher(key);
                                                    playTeacherIntroductionAudio(key);
                                                }
                                            }}
                                            style={{
                                                border: chineseTeacher === key ? '3px solid #1976d2' : '3px solid transparent',
                                                borderRadius: '50%',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            <img
                                                src={src}
                                                alt={key}
                                                style={{
                                                    width: '50px',
                                                    height: '50px',
                                                    objectFit: 'contain',
                                                    borderRadius: '50%',
                                                    display: 'block',
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <FormRow label="Audio speed (fast)">
                                <Checkbox
                                    checked={isAudioSpeedFast}
                                    onChange={(e) => setIsAudioSpeedFast(e.target.checked)}
                                    inputProps={{ 'aria-label': 'Audio speed (fast)' }}
                                />
                            </FormRow>

                            {avatar && !['/avatars/avatar1.png', '/avatars/avatar2.png'].includes(avatar) && (
                                <FormRow label="Current avatar">
                                    <img src={avatar} alt="" style={{ width: 50, height: 50, objectFit: 'contain' }} />
                                </FormRow>
                            )}

                            <FormRow label="Avatar">
                                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5em' }}>
                                    {avatarOptions.map((src) => (
                                        <img
                                            key={src}
                                            src={src}
                                            alt="Avatar option"
                                            style={{
                                                width: '50px',
                                                height: '50px',
                                                borderRadius: '50%',
                                                cursor: 'pointer',
                                                border: avatar === src ? '3px solid #1976d2' : '3px solid transparent',
                                            }}
                                            onClick={() => setAvatar(src)}
                                        />
                                    ))}
                                    <label
                                        htmlFor="avatar-upload"
                                        style={{
                                            cursor: 'pointer',
                                            border: '1px solid #ccc',
                                            padding: '6px 12px',
                                            borderRadius: '6px',
                                            fontSize: 14,
                                        }}
                                    >
                                        Upload image
                                    </label>
                                    <input
                                        type="file"
                                        id="avatar-upload"
                                        accept="image/*"
                                        style={{ display: 'none' }}
                                        onChange={handleAvatarChange}
                                    />
                                </div>
                            </FormRow>

                            <FormRow label="Required audio only">
                                <Checkbox
                                    checked={onlyRequiredAudio}
                                    onChange={(e) => setOnlyRequiredAudio(e.target.checked)}
                                    inputProps={{ 'aria-label': 'Required audio only' }}
                                />
                            </FormRow>

                            <FormRow label="Grading difficulty">
                                <Select
                                    value={scoringLevel}
                                    onChange={(e) => setScoringLevel(e.target.value)}
                                    size="small"
                                    sx={{ minWidth: 200 }}
                                >
                                    <MenuItem value={ScoringLevel.Easy}>
                                        {dialogPromptScoringLevel ? dialogPromptScoringLevel.no_button_text : 'Not Bad'}
                                    </MenuItem>
                                    <MenuItem value={ScoringLevel.Medium}>
                                        {dialogPromptScoringLevel ? dialogPromptScoringLevel.maybe_button_text : 'Strong'}
                                    </MenuItem>
                                    <MenuItem value={ScoringLevel.Hard}>
                                        {dialogPromptScoringLevel ? dialogPromptScoringLevel.yes_button_text : 'Pro'}
                                    </MenuItem>
                                </Select>
                            </FormRow>
                        </Stack>

                        {settingsError && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {settingsError}
                            </Alert>
                        )}
                        {settingsSuccess && (
                            <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSettingsSuccess('')}>
                                {settingsSuccess}
                            </Alert>
                        )}

                        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
                            <Button type="submit" variant="contained" disabled={savingSettings || !settingsDirty}>
                                {savingSettings ? <CircularProgress size={22} color="inherit" /> : 'Save preferences'}
                            </Button>
                        </Stack>
                    </Paper>

                    <Paper
                        component="form"
                        elevation={0}
                        onSubmit={handleSaveProfile}
                        sx={{ p: 3, overflow: 'hidden', ...pageWhiteCardSx }}
                    >
                        <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Profile & sign-in
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Name, email, and password. You must enter your <strong>current password</strong> to confirm any
                            change in this section.
                        </Typography>

                        <Stack spacing={2.5}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <TextField
                                    label="First name"
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    fullWidth
                                    autoComplete="given-name"
                                />
                                <TextField
                                    label="Last name"
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    fullWidth
                                    autoComplete="family-name"
                                />
                            </Stack>

                            <Stack spacing={1}>
                                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                                    <TextField
                                        label="Email address"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        fullWidth
                                        required
                                        autoComplete="email"
                                    />
                                    <Chip
                                        size="small"
                                        label={emailVerified ? 'Verified' : 'Not verified'}
                                        color={emailVerified ? 'success' : 'warning'}
                                        variant="outlined"
                                    />
                                </Stack>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    If you change your email, click <strong>Save profile</strong> first. We will send a verification link to the
                                    new address. Until you verify, some features may stay limited.
                                </Typography>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Button
                                        type="button"
                                        variant="text"
                                        size="small"
                                        onClick={handleValidateEmail}
                                        disabled={validateEmailClicked}
                                    >
                                        Resend verification email
                                    </Button>
                                    {validateEmailClicked && <CircularProgress size={18} />}
                                </Stack>
                            </Stack>

                            <Divider sx={{ my: 1 }} />

                            <Typography variant="subtitle2" component="p" color="text.secondary">
                                Change password (optional)
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: -1 }}>
                                Leave both fields blank to keep your current password.
                            </Typography>
                            <TextField
                                label="New password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                type={showNewPassword ? 'text' : 'password'}
                                fullWidth
                                autoComplete="new-password"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowNewPassword((v) => !v)} edge="end" aria-label="toggle new password visibility">
                                                {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <TextField
                                label="Confirm new password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                type={showNewPassword ? 'text' : 'password'}
                                fullWidth
                                autoComplete="new-password"
                            />

                            <Divider sx={{ my: 1 }} />

                            <TextField
                                label="Current password (required to save changes above)"
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                type={showCurrentPassword ? 'text' : 'password'}
                                fullWidth
                                required
                                autoComplete="current-password"
                                helperText={
                                    profileDirty
                                        ? 'Required whenever you change your name, email, or set a new password.'
                                        : 'Shown when you start editing profile fields or set a new password.'
                                }
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowCurrentPassword((v) => !v)} edge="end" aria-label="toggle current password visibility">
                                                {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Stack>

                        {profileError && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {profileError}
                            </Alert>
                        )}
                        {profileSuccess && (
                            <Alert severity="success" sx={{ mt: 2 }} onClose={() => setProfileSuccess('')}>
                                {profileSuccess}
                            </Alert>
                        )}

                        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
                            <Button type="submit" variant="contained" color="secondary" disabled={savingProfile || !profileDirty}>
                                {savingProfile ? <CircularProgress size={22} color="inherit" /> : 'Save profile'}
                            </Button>
                        </Stack>
                    </Paper>

                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block' }}>
                        Trouble signing in?{' '}
                        <Link href="/reset_password_email">Reset password via email</Link>
                    </Typography>
                </Stack>
            )}

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </div>
    );
}

function FormRow({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
            <Typography component="span" sx={{ width: 140, flexShrink: 0 }}>
                {label}
            </Typography>
            {children}
        </div>
    );
}
